import { Table } from "antd";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useVT } from "virtualizedtableforantd4";
import { TableColumnSettings } from "~components/TableColumnSettings/TableColumnSettings.tsx";
import type { CompetitorTypes } from "~enums/competitorTypes";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useTableColumns } from "~hooks/useTableColumns.ts";
import { useFixtureTableColumns } from "../hooks";
import type { FixtureTransformed } from "../types";
import { UpsertFixtureModal } from "./UpsertFixtureModal";

export interface IFixtureListProperties {
  clientFixtures: any[];
  competitorType: CompetitorTypes;
  isLoading: boolean;
  massEditMode: boolean;
}

const TableViewHeight = 500;

export const FixtureList = (properties: IFixtureListProperties) => {
  const [inlineEditFixtureId, setInlineEditFixtureId] = useState<string>("");
  const [modalEditFixture, setModalEditFixture] = useState<FixtureTransformed | null>(null);
  const [vt] = useVT(() => ({ scroll: { y: TableViewHeight } }), []);
  const { sport } = useParams();
  const { uiConfiguration } = useSportConfiguration();
  const tableConfiguration = uiConfiguration.pages.fixtures.tableColumnGroups;
  const columnSettingsLocalStorageKey = `${sport}.tables.settings.columns.fixtures`;
  const sorterLocalStorageKey = `${sport}.tables.settings.sort.fixtures`;

  const onLockUnlockClickedHandler = (fixtureId: string) => {
    if (fixtureId === inlineEditFixtureId) {
      setInlineEditFixtureId("");
    }
  };

  const tableColumns = useFixtureTableColumns(
    properties.massEditMode,
    inlineEditFixtureId,
    setInlineEditFixtureId,
    setModalEditFixture,
    onLockUnlockClickedHandler,
  );

  const { columnsToShow, setSelectedColumns, setSortColumn } = useTableColumns({
    columnSettingsConfig: {
      columnSettingsLocalStorageKey,
      tableConfiguration,
    },
    columnSortConfig: {
      sorterLocalStorageKey: sorterLocalStorageKey,
      defaultSorter: {
        columnKey: "startDate",
        order: "descend",
      },
    },
    tableColumns: tableColumns,
  });

  useEffect(() => {
    if (properties.massEditMode) {
      setInlineEditFixtureId("");
    }
  }, [properties.massEditMode]);

  return (
    <>
      <div className="fixtures-column-settings">
        <TableColumnSettings
          setSelectedColumns={setSelectedColumns}
          tableConfiguration={tableConfiguration}
          localStorageKey={columnSettingsLocalStorageKey}
          columns={tableColumns.map((x) => ({ key: x.key, title: x.title }))}
        />
      </div>
      <Table
        columns={columnsToShow}
        dataSource={properties.clientFixtures}
        rowKey={"fixtureId"}
        loading={properties.isLoading}
        size={"small"}
        className={"fixtures-table"}
        scroll={{ y: TableViewHeight }}
        components={vt}
        pagination={false}
        sticky={true}
        tableLayout="fixed"
        onChange={(_, __, sorter) => setSortColumn(sorter)}
        sortDirections={["ascend", "descend", "ascend"]}
      />
      {modalEditFixture && <UpsertFixtureModal fixture={modalEditFixture} onClose={() => setModalEditFixture(null)} />}
    </>
  );
};
